import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  RotateCcw,
} from "lucide-react"
import { useMutation } from "convex/react"
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from "react"
import { api } from "../../convex/_generated/api"

type Point = {
  x: number
  y: number
}

type RewardFood = Point & {
  rewardIndex: number
}

type Direction = "up" | "right" | "down" | "left"

const boardSize = 13
const tickMs = 122
const initialSnake: Point[] = [
  { x: 6, y: 6 },
  { x: 5, y: 6 },
  { x: 4, y: 6 },
]
const initialFood: RewardFood = { x: 9, y: 6, rewardIndex: 0 }

const teaserLines = [
  "Wild Pass is tuning up behind the curtain.",
  "First drops go to the people who find the door early.",
  "A little arcade dust, a little prize energy, not much else to say yet.",
]

const rewardDrops: Array<{
  name: string
  src: string
}> = [
  {
    name: "Cold one",
    src: "/rewards/beer.png",
  },
  {
    name: "Tee drop",
    src: "/rewards/shirt.png",
  },
  {
    name: "Cap drop",
    src: "/rewards/hat.png",
  },
  {
    name: "Slice",
    src: "/rewards/pizza.png",
  },
  {
    name: "Snack",
    src: "/rewards/nachos.png",
  },
  {
    name: "Mystery",
    src: "/rewards/mystery.png",
  },
]

const directionDelta: Record<Direction, Point> = {
  up: { x: 0, y: -1 },
  right: { x: 1, y: 0 },
  down: { x: 0, y: 1 },
  left: { x: -1, y: 0 },
}

const oppositeDirection: Record<Direction, Direction> = {
  up: "down",
  right: "left",
  down: "up",
  left: "right",
}

const directionButtons: Array<{
  direction: Direction
  Icon: typeof ArrowUp
  className: string
}> = [
  { direction: "up", Icon: ArrowUp, className: "col-start-2 row-start-1" },
  { direction: "left", Icon: ArrowLeft, className: "col-start-1 row-start-2" },
  { direction: "right", Icon: ArrowRight, className: "col-start-3 row-start-2" },
  { direction: "down", Icon: ArrowDown, className: "col-start-2 row-start-3" },
]

function samePoint(a: Point, b: Point) {
  return a.x === b.x && a.y === b.y
}

function pointKey(point: Point) {
  return `${point.x}:${point.y}`
}

function createFood(snake: Point[], currentFood: RewardFood): RewardFood {
  const occupied = new Set(snake.map(pointKey))
  const openCells: Point[] = []

  for (let y = 0; y < boardSize; y += 1) {
    for (let x = 0; x < boardSize; x += 1) {
      const point = { x, y }
      if (!occupied.has(pointKey(point))) {
        openCells.push(point)
      }
    }
  }

  if (openCells.length === 0) {
    return currentFood
  }

  const nextCell = openCells[Math.floor(Math.random() * openCells.length)]
  const nextRewardOffset =
    rewardDrops.length > 1 ? 1 + Math.floor(Math.random() * (rewardDrops.length - 1)) : 0

  return {
    ...nextCell,
    rewardIndex: (currentFood.rewardIndex + nextRewardOffset) % rewardDrops.length,
  }
}

function resetGame() {
  return {
    snake: initialSnake.map((segment) => ({ ...segment })),
    food: { ...initialFood },
    direction: "right" as Direction,
    queuedDirection: "right" as Direction,
    score: 0,
    gameOver: false,
  }
}

function directionFromKey(key: string): Direction | null {
  const keyMap: Partial<Record<string, Direction>> = {
    ArrowUp: "up",
    ArrowRight: "right",
    ArrowDown: "down",
    ArrowLeft: "left",
    w: "up",
    d: "right",
    s: "down",
    a: "left",
    W: "up",
    D: "right",
    S: "down",
    A: "left",
  }

  return keyMap[key] ?? null
}

export function LandingSnake() {
  const joinLeadList = useMutation(api.landingLeads.join)
  const [snake, setSnake] = useState<Point[]>(initialSnake)
  const [food, setFood] = useState<RewardFood>(initialFood)
  const [score, setScore] = useState(0)
  const [bestScore, setBestScore] = useState(0)
  const [gameOver, setGameOver] = useState(false)
  const [hasPlayerInput, setHasPlayerInput] = useState(false)
  const [teaserIndex, setTeaserIndex] = useState(0)
  const [leadEmail, setLeadEmail] = useState("")
  const [leadStatus, setLeadStatus] = useState<
    "idle" | "submitting" | "success" | "error"
  >("idle")
  const [leadError, setLeadError] = useState<string | null>(null)
  const directionRef = useRef<Direction>("right")
  const queuedDirectionRef = useRef<Direction>("right")
  const foodRef = useRef<RewardFood>(initialFood)
  const scoreRef = useRef(0)
  const gameOverRef = useRef(false)
  const hasPlayerInputRef = useRef(false)
  const nextTeaserIndexRef = useRef(0)
  const touchStart = useRef<Point | null>(null)

  const snakeCells = useMemo(() => new Set(snake.map(pointKey)), [snake])
  const headKey = pointKey(snake[0])
  const currentReward = rewardDrops[food.rewardIndex]
  const showLeadCapture = gameOver && hasPlayerInput

  const restart = useCallback((nextInput?: Direction) => {
    const next = resetGame()
    directionRef.current = next.direction
    queuedDirectionRef.current = next.queuedDirection
    if (nextInput && oppositeDirection[next.direction] !== nextInput) {
      queuedDirectionRef.current = nextInput
    }
    foodRef.current = next.food
    scoreRef.current = next.score
    gameOverRef.current = next.gameOver
    hasPlayerInputRef.current = Boolean(nextInput)
    setSnake(next.snake)
    setFood(next.food)
    setScore(next.score)
    setGameOver(next.gameOver)
    setHasPlayerInput(Boolean(nextInput))
    setLeadStatus("idle")
    setLeadError(null)
  }, [])

  const queueDirection = useCallback(
    (nextDirection: Direction) => {
      hasPlayerInputRef.current = true
      setHasPlayerInput(true)

      if (gameOverRef.current) {
        restart(nextDirection)
        return
      }

      if (oppositeDirection[directionRef.current] === nextDirection) {
        return
      }
      queuedDirectionRef.current = nextDirection
    },
    [restart],
  )

  useEffect(() => {
    const handleWindowKeyDown = (event: KeyboardEvent) => {
      const target = event.target
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable ||
          ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))
      ) {
        return
      }

      if (event.key === "Enter") {
        event.preventDefault()
        restart()
        return
      }

      const nextDirection = directionFromKey(event.key)
      if (!nextDirection) {
        return
      }

      event.preventDefault()
      queueDirection(nextDirection)
    }

    window.addEventListener("keydown", handleWindowKeyDown)
    return () => window.removeEventListener("keydown", handleWindowKeyDown)
  }, [queueDirection, restart])

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (gameOverRef.current) {
        return
      }

      setSnake((currentSnake) => {
        const direction = directionRef.current
        const queuedDirection = queuedDirectionRef.current
        const currentFood = foodRef.current
        const currentScore = scoreRef.current
        const nextDirection =
          oppositeDirection[direction] === queuedDirection
            ? direction
            : queuedDirection
        const delta = directionDelta[nextDirection]
        const head = currentSnake[0]
        const nextHead = {
          x: head.x + delta.x,
          y: head.y + delta.y,
        }
        const ateFood = samePoint(nextHead, currentFood)
        const bodyToCheck = ateFood ? currentSnake : currentSnake.slice(0, -1)
        const crashed =
          nextHead.x < 0 ||
          nextHead.x >= boardSize ||
          nextHead.y < 0 ||
          nextHead.y >= boardSize ||
          bodyToCheck.some((segment) => samePoint(segment, nextHead))

        if (crashed) {
          gameOverRef.current = true
          if (hasPlayerInputRef.current) {
            const nextTeaserIndex = nextTeaserIndexRef.current
            setTeaserIndex(nextTeaserIndex)
            nextTeaserIndexRef.current = (nextTeaserIndex + 1) % teaserLines.length
          }
          setGameOver(true)
          setBestScore((currentBest) => Math.max(currentBest, currentScore))
          return currentSnake
        }

        const nextSnake = [nextHead, ...currentSnake]
        if (ateFood) {
          const nextScore = currentScore + 10
          const nextFood = createFood(nextSnake, currentFood)
          scoreRef.current = nextScore
          foodRef.current = nextFood
          setScore(nextScore)
          setBestScore((currentBest) => Math.max(currentBest, nextScore))
          setFood(nextFood)
        } else {
          nextSnake.pop()
        }

        directionRef.current = nextDirection
        return nextSnake
      })
    }, tickMs)

    return () => window.clearInterval(timer)
  }, [])

  const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    const touch = event.touches[0]
    touchStart.current = { x: touch.clientX, y: touch.clientY }
  }

  const handleTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    const start = touchStart.current
    const touch = event.changedTouches[0]
    touchStart.current = null
    if (!start || !touch) {
      return
    }

    const dx = touch.clientX - start.x
    const dy = touch.clientY - start.y
    if (Math.max(Math.abs(dx), Math.abs(dy)) < 18) {
      return
    }

    queueDirection(
      Math.abs(dx) > Math.abs(dy)
        ? dx > 0
          ? "right"
          : "left"
        : dy > 0
          ? "down"
          : "up",
    )
  }

  const submitLead = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const email = leadEmail.trim()
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setLeadStatus("error")
      setLeadError("Use a valid email.")
      return
    }

    setLeadStatus("submitting")
    setLeadError(null)

    try {
      await joinLeadList({ email, score })
      setLeadStatus("success")
    } catch (error) {
      setLeadStatus("error")
      setLeadError(error instanceof Error ? error.message : "Could not join yet.")
    }
  }

  return (
    <aside
      aria-label="Wild Guess arcade game"
      className="landing-snake relative mt-8 w-full max-w-[22rem] overflow-hidden rounded-lg border border-white/14 bg-[#11113a]/78 p-3 text-left shadow-[0_30px_90px_-54px_rgba(0,0,0,0.95)] backdrop-blur-md sm:mt-10"
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="h-2.5 w-2.5 rounded-full bg-[#c8e840] shadow-[0_0_18px_rgba(200,232,64,0.86)]" />
          <div className="min-w-0">
            <p className="text-[0.62rem] font-extrabold uppercase tracking-[0.2em] text-white/62">
              Wild Pass
            </p>
            <p className="truncate text-[0.7rem] font-extrabold uppercase tracking-[0.16em] text-[#c8e840]">
              Drop: {currentReward.name}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs font-extrabold uppercase tracking-[0.18em] text-[#38bdf8]">
            WP {score.toString().padStart(3, "0")}
          </span>
          <button
            type="button"
            aria-label="Restart"
            className="grid h-8 w-8 place-items-center rounded-full border border-white/12 bg-white/8 text-white/76 transition hover:border-[#c8e840]/60 hover:text-[#c8e840]"
            onClick={() => restart()}
          >
            <RotateCcw aria-hidden="true" size={15} strokeWidth={3} />
          </button>
        </div>
      </div>
      <div className="mb-3 flex items-center justify-between rounded-full border border-white/10 bg-white/[0.045] px-3 py-1.5">
        <span className="text-[0.62rem] font-extrabold uppercase tracking-[0.18em] text-white/48">
          Best {bestScore.toString().padStart(3, "0")}
        </span>
        <span className="text-[0.62rem] font-extrabold uppercase tracking-[0.18em] text-white/48">
          Enter resets
        </span>
      </div>

      <div
        className="relative aspect-square overflow-hidden rounded-md border border-white/10 bg-[#080822] p-3"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div
          aria-hidden="true"
          className="grid h-full w-full gap-[3px]"
          style={{
            gridTemplateColumns: `repeat(${boardSize}, minmax(0, 1fr))`,
            gridTemplateRows: `repeat(${boardSize}, minmax(0, 1fr))`,
          }}
        >
          {Array.from({ length: boardSize * boardSize }, (_, index) => {
            const point = { x: index % boardSize, y: Math.floor(index / boardSize) }
            const key = pointKey(point)
            const isHead = key === headKey
            const isSnake = snakeCells.has(key)
            const isFood = samePoint(point, food)

            return (
              <div
                key={key}
                className={[
                  "grid place-items-center rounded-[3px]",
                  isHead
                    ? "bg-[#f8f7ff] shadow-[0_0_18px_rgba(248,247,255,0.55)]"
                    : isSnake
                      ? "bg-[#c8e840]"
                      : isFood
                        ? "bg-[#ee3f8f]/10"
                        : "bg-white/[0.045]",
                ].join(" ")}
              >
                {isFood ? (
                  <img
                    src={currentReward.src}
                    alt=""
                    aria-hidden="true"
                    className="pointer-events-none h-[160%] w-[160%] max-w-none object-contain drop-shadow-[0_0_8px_rgba(248,247,255,0.45)]"
                  />
                ) : null}
              </div>
            )
          })}
        </div>
        {gameOver ? (
          <div className="absolute inset-2 grid place-items-center rounded-md bg-[#09091f]/82 px-4 text-center backdrop-blur-[2px]">
            {showLeadCapture ? (
              <div className="w-full max-w-[17.5rem]">
                <p className="text-[0.62rem] font-extrabold uppercase tracking-[0.2em] text-[#c8e840]">
                  Nice run
                </p>
                <p className="mt-2 min-h-[3rem] text-sm font-extrabold leading-5 text-white">
                  {teaserLines[teaserIndex]}
                </p>
                {leadStatus === "success" ? (
                  <div className="mt-4 rounded-md border border-[#c8e840]/35 bg-[#c8e840]/12 px-3 py-3">
                    <p className="text-sm font-extrabold text-[#f8f7ff]">
                      Your Wild Pass is reserved.
                    </p>
                    <button
                      type="button"
                      className="mt-3 rounded-full border border-white/14 bg-white/10 px-4 py-2 text-[0.62rem] font-extrabold uppercase tracking-[0.14em] text-white/82 transition active:scale-[0.98]"
                      onClick={() => restart()}
                    >
                      Again
                    </button>
                  </div>
                ) : (
                  <form className="mt-4 space-y-2" onSubmit={submitLead}>
                    <label className="block text-left">
                      <span className="sr-only">Email address</span>
                      <input
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        autoCapitalize="none"
                        autoCorrect="off"
                        value={leadEmail}
                        onChange={(event) => setLeadEmail(event.target.value)}
                        placeholder="you@example.com"
                        className="h-11 w-full rounded-full border border-white/12 bg-white/10 px-4 text-sm font-bold text-white outline-none transition placeholder:text-white/36 focus:border-[#c8e840]/70 focus:ring-2 focus:ring-[#c8e840]/25"
                      />
                    </label>
                    <button
                      type="submit"
                      disabled={leadStatus === "submitting"}
                      className="h-11 w-full rounded-full border border-[#c8e840]/60 bg-[#c8e840] px-4 text-[0.68rem] font-extrabold uppercase tracking-[0.14em] text-[#09091f] shadow-[0_18px_42px_-22px_rgba(200,232,64,0.9)] transition active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {leadStatus === "submitting" ? "Sending" : "Reserve your Wild Pass"}
                    </button>
                    {leadError ? (
                      <p className="text-xs font-bold text-[#ff8ab8]">{leadError}</p>
                    ) : null}
                    <p className="px-2 text-[0.58rem] font-bold leading-4 text-white/48">
                      Occasional Wild Pass launch updates. Unsubscribe anytime.
                    </p>
                    <button
                      type="button"
                      className="text-[0.62rem] font-extrabold uppercase tracking-[0.14em] text-white/48 transition hover:text-white"
                      onClick={() => restart()}
                    >
                      Again
                    </button>
                  </form>
                )}
              </div>
            ) : (
              <button
                type="button"
                className="rounded-full border border-[#c8e840]/60 bg-[#c8e840] px-5 py-2 text-sm font-extrabold uppercase tracking-[0.12em] text-[#09091f] shadow-[0_18px_42px_-22px_rgba(200,232,64,0.9)]"
                onClick={() => restart()}
              >
                Again
              </button>
            )}
          </div>
        ) : null}
      </div>

      <div className="mt-4 grid grid-cols-[3.25rem_3.25rem_3.25rem] grid-rows-[3.25rem_3.25rem_3.25rem] justify-center gap-2 sm:hidden">
        {directionButtons.map(({ direction: buttonDirection, Icon, className }) => (
          <button
            key={buttonDirection}
            type="button"
            aria-label={`Move ${buttonDirection}`}
            className={`grid h-[3.25rem] w-[3.25rem] touch-manipulation select-none place-items-center rounded-full border border-white/14 bg-white/10 text-white/86 shadow-[0_12px_34px_-24px_rgba(0,0,0,0.9)] transition active:scale-95 active:border-[#c8e840]/60 active:bg-white/16 ${className}`}
            onClick={() => queueDirection(buttonDirection)}
          >
            <Icon aria-hidden="true" size={23} strokeWidth={3} />
          </button>
        ))}
      </div>
      <p className="mt-3 text-center text-[0.58rem] font-bold uppercase tracking-[0.12em] text-white/30">
        Preview points only. Not redeemable.
      </p>
    </aside>
  )
}
